// ./components/PageTransition.js
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

import logo from "../assets/logo-1.png";

const PageTransition = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const hasVisited = sessionStorage.getItem("hasVisited");
    
    if (!hasVisited) {
      const timer = setTimeout(() => {
        setIsLoading(false);
        sessionStorage.setItem("hasVisited", "true");
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Gentle ocean waves with pink/purple/white
  const oceanWaves = [...Array(10)].map((_, i) => ({
    id: i,
    delay: i * 0.12,
    duration: 3 + (i * 0.15),
    maxScale: 1.6 + (i * 0.08),
    initialOpacity: 0.35 - (i * 0.02),
  }));

  return (
    <>
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-white overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-white to-purple-50" />
            <div className="absolute inset-0 opacity-5" style={{ backgroundSize: '40px 40px' }} />

            <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="text-center space-y-4">
                <div className="flex justify-center mb-8 relative">
                  <div className="relative inline-flex items-center justify-center">
                    
                    {/* Concentric wave rings - perfectly round */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      {oceanWaves.map((wave) => (
                        <motion.div
                          key={wave.id}
                          initial={{ scale: 1, opacity: wave.initialOpacity }}
                          animate={{
                            scale: [1, wave.maxScale, wave.maxScale + 0.15],
                            opacity: [wave.initialOpacity, wave.initialOpacity * 0.5, 0],
                          }}
                          transition={{
                            duration: wave.duration,
                            delay: wave.delay,
                            repeat: Infinity,
                            ease: "easeInOut",
                            times: [0, 0.6, 1],
                          }}
                          className="absolute rounded-full"
                          style={{
                            width: "280px",
                            height: "280px",
                            borderRadius: "50%",
                            border: `1.5px solid rgba(236, 72, 153, ${0.3 - wave.id * 0.02})`,
                            background: `radial-gradient(circle, rgba(255,255,255,0.1), rgba(236,72,153,0.05), transparent)`,
                            boxShadow: `0 0 8px rgba(236,72,153,0.15)`,
                            filter: `blur(${wave.id * 0.3}px)`,
                          }}
                        />
                      ))}
                    </div>

                    {/* Fluid organic wave rings - changing shape subtly */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      {[0, 1, 2, 3, 4].map((ring) => (
                        <motion.div
                          key={`fluid-${ring}`}
                          initial={{ scale: 1, opacity: 0.25 }}
                          animate={{
                            scale: [1, 1.4 + ring * 0.06, 1.7 + ring * 0.08],
                            opacity: [0.25, 0.12, 0],
                            borderRadius: [
                              "50%",
                              "48% 52% 52% 48% / 51% 49% 51% 49%",
                              "45% 55% 50% 50% / 52% 48% 52% 48%",
                              "50%"
                            ],
                          }}
                          transition={{
                            duration: 3.2 + ring * 0.2,
                            delay: ring * 0.18,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          className="absolute"
                          style={{
                            width: "290px",
                            height: "290px",
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, rgba(255,255,255,0.2), rgba(236,72,153,0.08), rgba(168,85,247,0.08))`,
                            border: `0.5px solid rgba(236,72,153,0.2)`,
                            filter: `blur(${2 + ring}px)`,
                          }}
                        />
                      ))}
                    </div>

                    {/* Soft gradient pulses - deep color transitions */}
                    {[0, 1, 2, 3].map((layer) => (
                      <motion.div
                        key={`gradient-${layer}`}
                        initial={{ scale: 1, opacity: 0.2 }}
                        animate={{
                          scale: [1, 1.3 + layer * 0.05, 1.6 + layer * 0.1],
                          opacity: [0.2, 0.08, 0],
                        }}
                        transition={{
                          duration: 2.8 + layer * 0.3,
                          delay: layer * 0.22,
                          repeat: Infinity,
                          ease: "easeOut",
                        }}
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          width: "270px",
                          height: "270px",
                          borderRadius: "50%",
                          background: `radial-gradient(circle, 
                            rgba(255,255,255,0.15), 
                            rgba(236,72,153,0.08), 
                            rgba(168,85,247,0.05), 
                            transparent)`,
                          filter: `blur(${6 + layer * 2}px)`,
                        }}
                      />
                    ))}

                    {/* Micro-ripples - very subtle */}
                    {[0, 1, 2, 3, 4, 5].map((ripple) => (
                      <motion.div
                        key={`ripple-${ripple}`}
                        initial={{ scale: 1, opacity: 0.12 }}
                        animate={{
                          scale: [1, 1.2 + ripple * 0.04, 1.35 + ripple * 0.06],
                          opacity: [0.12, 0.05, 0],
                        }}
                        transition={{
                          duration: 1.8 + ripple * 0.1,
                          delay: ripple * 0.08,
                          repeat: Infinity,
                          ease: "easeIn",
                        }}
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          width: "285px",
                          height: "285px",
                          borderRadius: "50%",
                          border: `0.3px solid rgba(236,72,153,0.3)`,
                          filter: `blur(1px)`,
                        }}
                      />
                    ))}

                    {/* Logo Container */}
                    <div className="relative z-10 w-64 h-64 md:w-80 md:h-80">
                      <div className="w-full h-full rounded-full overflow-hidden shadow-sm">
                        <img 
                          src={logo}
                          alt="Petals Paradise Logo"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <h1 className="text-5xl md:text-7xl font-light tracking-tight">
                    <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent font-serif">
                      Petals
                    </span>
                    <span className="text-gray-800 font-serif italic mx-2"></span>
                      <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent font-serif">
                    Paradise
                    </span>
                  </h1>
                </motion.div>

                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "120px" }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent mx-auto"
                />

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1.1 }}
                  className="space-y-2"
                >
                  <p className="text-xl md:text-2xl text-gray-600 font-light ">
                    Where flowers speak the language of love
                  </p>
                </motion.div>
              </div>

              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: "200px", opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.7 }}
                className="absolute -bottom-16 left-1/2 -translate-x-1/2"
              >
                <div className="relative">
                  <div className="h-0.5 w-48 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 1.8, delay: 1.9, ease: "easeInOut" }}
                      className="h-full bg-gradient-to-r from-pink-400 to-purple-400 rounded-full"
                    />
                  </div>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 3.4 }}
                    className="absolute -right-1 -top-1 w-2 h-2 rounded-full bg-pink-400"
                  />
                </div>
              </motion.div>
            </div>

            <motion.div
              animate={{ opacity: [0.15, 0.25, 0.15] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-r from-pink-200 to-purple-200 rounded-full blur-3xl opacity-20"
            />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PageTransition;